import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import '@angular/common/locales/global/de';
import '@angular/compiler';
import {
  APP_INITIALIZER,
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  Router,
  RouteReuseStrategy,
  TitleStrategy,
  withInMemoryScrolling,
} from '@angular/router';
import { Locale } from '@api-types/enums/locale.enum';
import { provideKeycloakAngular } from '@core/config/keycloak.config';
import { IncludeBearerTokenInterceptor } from '@core/interceptors/include-bearer-token-interceptor';
import { provideSse } from '@core/sse/provide-sse';
import { provideChat } from '@components/chat/services/provide-chat';
import { authReducer } from '@core/store/auth/auth.reducer';
import { featureFlagReducer } from '@core/store/feature-flags/feature-flags.reducer';
import { environment } from '@environments/environment';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import {
  AgChartsEnterpriseModule,
  LicenseManager as ChartLicenseManager,
} from 'ag-charts-enterprise';
import {
  AllEnterpriseModule,
  LicenseManager,
  ModuleRegistry,
  setupAgTestIds,
} from 'ag-grid-enterprise';
import { AppComponent } from 'core/app.component';
import { ReusableRouteStrategy } from 'core/services/reusable-route.strategy';
import { APP_ROUTES } from 'core/app.routes';
import { MappedTranslationLoader } from 'core/config/mapped-translation.loader';
import { LanguageInterceptor } from 'core/interceptors/language.interceptor';
import { provideHighcharts } from 'highcharts-angular';
import { provideMarkdown } from 'ngx-markdown';
import { provideToastr } from 'ngx-toastr';
import { TranslatedTitleStrategy } from '@core/strategy/translated-title.strategy';
import { DATE_FORMATTER, UI_TRANSLATIONS } from '@libs/ui';
import { DateUtilsService } from '@core/utils/date-utils.service';
import { LocaleService } from '@core/services/locale.service';
import { format } from 'date-fns';
import { de, enGB, enUS } from 'date-fns/locale';

const highchartsModules = () => [
  import('highcharts/modules/stock'),
  import('highcharts/modules/exporting'),
  import('highcharts/modules/export-data'),
  import('highcharts/modules/accessibility'),
  import('highcharts/modules/pattern-fill'),
  import('highcharts/modules/heatmap'),
  import('highcharts/highcharts-more'),
];

ModuleRegistry.registerModules([
  AllEnterpriseModule.with(AgChartsEnterpriseModule),
]);
ChartLicenseManager.setLicenseKey(environment.agGridLicenseKey);
LicenseManager.setLicenseKey(environment.agGridLicenseKey);

if (environment.environment !== 'production') {
  setupAgTestIds();
}

const translationLoaderFactory = (http: HttpClient): TranslateLoader =>
  new MappedTranslationLoader(http);

if (
  environment.environment === 'production' ||
  environment.environment === 'staging'
) {
  enableProdMode();
}

if (window.location.hostname === 'localhost') {
  console.warn('Running in development mode - Sentry disabled');
} else {
  Sentry.init({
    dsn: environment.sentryDsn,
    environment: environment.environment,
    release: environment.version,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllInputs: false,
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.browserProfilingIntegration(),
    ],
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
    tracesSampleRate: 1,
    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Set profilesSampleRate to 1.0 to profile every transaction.
    // Since profilesSampleRate is relative to tracesSampleRate,
    // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
    // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
    // results in 25% of transactions being profiled (0.5*0.5=0.25)
    profilesSampleRate: 1.0,
  });
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      APP_ROUTES,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
    {
      provide: TitleStrategy,
      useClass: TranslatedTitleStrategy,
    },
    provideToastr(),
    provideStore({
      auth: authReducer,
      featureFlags: featureFlagReducer,
      router: routerReducer,
    }),
    provideRouterStore(),
    ...(environment.environment === 'production' ||
    environment.environment === 'staging'
      ? []
      : [provideStoreDevtools()]),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideHighcharts({
      instance: () => import('highcharts').then((m) => m.default),
      modules: highchartsModules,
    }),
    provideKeycloakAngular(),
    ...provideSse(), // IMPORTANT: has to be set after provideKeycloakAngular since it requires token from keycloak
    provideChat(),
    provideMarkdown(),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: translationLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ),
    {
      provide: RouteReuseStrategy,
      useClass: ReusableRouteStrategy,
    },
    { provide: LOCALE_ID, useValue: Locale.GERMAN },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: IncludeBearerTokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => undefined,
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: UI_TRANSLATIONS,
      useFactory: (
        translate: TranslateService,
        localeService: LocaleService,
      ) => ({
        get weekdays(): readonly [
          string,
          string,
          string,
          string,
          string,
          string,
          string,
        ] {
          // JSON order is Sun-first; picker expects Mon-first
          const keys = translate.instant(
            'HIGHCHARTS.SHORT_WEEKDAYS',
          ) as string[];
          return [
            keys[1],
            keys[2],
            keys[3],
            keys[4],
            keys[5],
            keys[6],
            keys[0],
          ] as readonly [
            string,
            string,
            string,
            string,
            string,
            string,
            string,
          ];
        },
        get locale() {
          switch (localeService.getCurrentUserLocale()) {
            case 'de-DE':
              return de;
            case 'en-US':
              return enUS;
            default:
              return enGB;
          }
        },
      }),
      deps: [TranslateService, LocaleService],
    },
    {
      provide: DATE_FORMATTER,
      useFactory:
        (svc: DateUtilsService) =>
        (date: Date, level: 'year' | 'month' | 'day') => {
          switch (level) {
            case 'year':
              return format(date, 'yyyy');
            case 'month':
              return svc.formatMonthYearBasedOnLanguage(date.toISOString());
            case 'day':
              return svc.formatDateBasedOnLanguage(date.toISOString());
          }
        },
      deps: [DateUtilsService],
    },
  ],
}).catch((err) => console.error(err));
